import React, { useState, useEffect } from 'react'
import {
  Box,
  Button,
  Typography,
  Tooltip,
  Link as MuiLink,
} from '@mui/material'
import { useNavigate, Link } from 'react-router'
import { ROUTES } from '../../routes/routeConfig'
import { getEmployeeList, getSSOToken, loginSSO } from '../../api/auth'
import { setUsername, getUserSessionId } from '../../utils/session'
import InfoIcon from '@mui/icons-material/Info'
import { GLOBAL_CONSTANTS } from "../../constants/globalConstants"
import { CommonModal } from '../common'
import MultiLogin from './MultiLogin'
import { PWC } from '../../assets/icons/PWC.jsx'
import CustomSelect from '../Returns/CustomSelect'
import { sanitizeForLog } from '../../constants/index'


const SSOLogin = () => {
  const [accounts, setAccounts] = useState([])
  const [selectedUser, setSelectedUser] = useState('')
  const [formError, setFormError] = useState('')
  const navigate = useNavigate()
  const [isMultiLoginModal, setIsMultiLoginModal] = useState(false)
  const userSessionId = getUserSessionId()

  // Fetch employees on mount
  useEffect(() => {
    if (!userSessionId) {
      navigate(ROUTES.Login, { replace: true })
      return
    }
    const fetchEmployees = async () => {
      try {
        const res = await getEmployeeList({ sessionId: userSessionId })
        const list = res?.data || []
        setAccounts(list)
        if (!list.length) {
          navigate(ROUTES.Login, { replace: true })
          return
        }

        if (list.length === 1) {
          const user = list[0]
          await loginSSO({ sessionId: userSessionId, userId: user.id })
          await getSSOToken({ sessionId: userSessionId })
          setUsername(user.userName)
          navigate(ROUTES.DASHBOARD, { replace: true })
          return
        }

      } catch (err) {
        console.error('Error fetching employee list:', sanitizeForLog(err?.message || 'Unknown error'))
        console.log(sanitizeForLog(err?.message || ''), "err.message in sso login");
        if (err?.message === "ALREADY_LOGGED_IN_MULTI_SESSION_NOT_ALLOWED" || err?.message === "ALREADY_LOGGED_IN") {
          setIsMultiLoginModal(true)
        } else {
          setFormError(err?.message || 'SSO login failed')
        }
        navigate(ROUTES.Login, { replace: true })
      }
    }
    fetchEmployees()
  }, [userSessionId, navigate])

  const handleSubmit = async () => {
    if (!selectedUser) {
      setFormError('Please select an account')
      return
    }

    const user = accounts.find(acc => acc.id === selectedUser)
    try {
      await loginSSO({ sessionId: userSessionId, userId: user.id })
      await getSSOToken({ sessionId: userSessionId })
      setUsername(user.userName)
      navigate(ROUTES.DASHBOARD, { replace: true })
    } catch (err) {
      console.error('SSO login error:', sanitizeForLog(err?.message || 'Unknown error'))
      console.log(sanitizeForLog(err?.message || ''), "err.message in sso login");
      if (err?.message === "ALREADY_LOGGED_IN_MULTI_SESSION_NOT_ALLOWED" || err?.message === "ALREADY_LOGGED_IN") {
        setIsMultiLoginModal(true)
      } else {
        setFormError(err?.message || 'SSO login failed')
      }
    }
  }

  const handleCancel = () => {
    navigate(ROUTES.LOGIN, { replace: true })
    window.location.reload()
  }

  const handleMultiLogin = async () => {
    const user = selectedUser
      ? accounts.find(acc => acc.id === selectedUser)
      : accounts[0]

    if (!user) {
      setFormError('No user selected')
      return
    }

    try {
      await loginSSO({ sessionId: userSessionId, userId: user.id })
      await getSSOToken({ sessionId: userSessionId, userAlreadyLogin: true })
      setUsername(user.userName)
      setIsMultiLoginModal(false)
      navigate(ROUTES.DASHBOARD, { replace: true })
    } catch (err) {
      if (
        err?.message === 'ALREADY_LOGGED_IN_MULTI_SESSION_NOT_ALLOWED' ||
        err?.message === 'ALREADY_LOGGED_IN'
      ) {
        setIsMultiLoginModal(true)
      } else {
        setFormError(err?.message || 'SSO login failed')
      }
    }
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'var(--bg-light-teal)',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: { xs: 2, md: 3 },
        }}
      >
        <PWC />
      </Box>

      {/* Main Content - Centered Form */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
        }}
      >
        <Box sx={{ width: '100%', maxWidth: '448px' }}>
          {/* Title */}
          <Box sx={{ mb: 2 }}>
            <Typography
              sx={{
                color: 'var(--forbrand-primary)',
                fontSize: '20px',
                fontWeight: 500,
              }}
            >
              Welcome to
            </Typography>

            <Typography
              variant="h6"
              sx={{
                fontFamily: 'Charter',
                fontWeight: 400,
                fontStyle: 'normal',
                fontSize: '40px',
                lineHeight: '110%',
                letterSpacing: '-2%',
                verticalAlign: 'middle',
                color: 'var(--forbrand-primary)',
              }}
            >
              Navigate Withholding Tax
            </Typography>
          </Box>

          {/* Form */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: '14px',
                color: 'var(--forbrand-primary)',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              Sign in to one of the following accounts:
              <Tooltip
                sx={{ ml: 1 }}
                placement="top"
                title={GLOBAL_CONSTANTS.LINKED_USER_COL_INFO}
              >
                <InfoIcon sx={{ fontSize: '18px' }} />
              </Tooltip>
            </Typography>

            <CustomSelect
              label="Select Account"
              value={selectedUser}
              onChange={e => {
                setSelectedUser(e.target.value)
                setFormError('')
              }}
              options={accounts}
              getOptionLabel={(option) => option.userName}
              getOptionValue={(option) => option.id}
              placeholder="Select an account"
              required
              error={!!formError}
              helperText={formError}
              fullWidth
            />

            <Box
              sx={{
                display: 'flex',
                gap: 2,
                justifyContent: 'end',
                alignItems: 'center',
                mt: 2,
              }}
            >
              <Link to={ROUTES.LOGIN} style={{ textDecoration: 'none' }}>
                <Typography
                  sx={{
                    color: 'var(--forbrand-primary)',
                    fontSize: '16px',
                    fontWeight: 500,
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                >
                  Back to Login
                </Typography>
              </Link>
              <Button
                variant="contained"
                onClick={handleSubmit}
                sx={{
                  textTransform: 'none',
                  fontSize: '18px',
                  fontWeight: 500,
                  boxShadow: 'none',
                  backgroundColor: 'var(--forbrand-bg-variant)',
                  color: 'var(--forbrand-brand-varient)',
                  px: 4,
                  py: 1.5,
                  '&:hover': {
                    boxShadow: 'none',
                  },
                }}
              >
                Login
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>

      <CommonModal
        open={isMultiLoginModal}
        showCloseButton={false}
        onClose={() => setIsMultiLoginModal(false)}
        title={'Login Detected'}
        maxWidth="sm"
        textColor={'red'}
      >
        <MultiLogin handleSubmit={handleMultiLogin} handleCancel={handleCancel} />
      </CommonModal>
    </Box>
  )
}

export default SSOLogin
